import { useState, useEffect, useCallback } from 'react';
import cache from '../services/cache';

/**
 * Custom hook for caching API responses
 */
export function useCache(key, fetcher, options = {}) {
  const {
    ttl = 5 * 60 * 1000, // 5 minutes default
    enabled = true,
    refresh = false,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!enabled) return;

    // Check cache first (unless force refresh)
    if (!forceRefresh && !refresh) {
      const cached = cache.get(key);
      if (cached !== null) {
        setData(cached);
        setLoading(false);
        setError(null);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      const result = await fetcher();
      setData(result);
      cache.set(key, result, ttl);
    } catch (err) {
      setError(err);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [key, ttl, enabled, refresh, fetcher]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const invalidate = useCallback(() => {
    cache.delete(key);
    fetchData(true);
  }, [key, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    invalidate,
  };
}

/**
 * Hook for manual cache management
 */
export function useCacheManager() {
  const get = useCallback((key) => {
    return cache.get(key);
  }, []);

  const set = useCallback((key, value, ttl) => {
    cache.set(key, value, ttl);
  }, []);

  const remove = useCallback((key) => {
    cache.delete(key);
  }, []);

  const clear = useCallback(() => {
    cache.clear();
  }, []);

  return { get, set, remove, clear };
}

